// Opening hours of the restaurant (owner 2026-11-03, DEC-030: the program shown in the public menu). Kept in the
// public profile as one compact string so the draft → publish flow and its field diff work unchanged:
//   "1=12:00-23:00;2=12:00-23:00;…;7=" — day 1 = Monday … 7 = Sunday; an empty day is closed; a day may hold
//   several intervals separated by "," (e.g. a lunch break); a closing time before the opening one ends after
//   midnight. The whole string empty = no program shown.

export const WEEK = [1, 2, 3, 4, 5, 6, 7] as const;
export type Weekday = (typeof WEEK)[number];
export type Interval = [open: string, close: string];
export type Week = Record<Weekday, Interval[]>;

const TIME = /^([01]\d|2[0-4]):([0-5]\d)$/;
/** "HH:MM" from 00:00 to 24:00 (24:00 = midnight at the end of the day). */
const valid = (t: string) => TIME.test(t) && (!t.startsWith("24") || t === "24:00");

export const emptyWeek = (): Week => ({ 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [] });

/** The week from its compact string; null when the string is malformed. */
export function parseHours(s: string): Week | null {
  const week = emptyWeek();
  if (!s.trim()) return week;
  const seen = new Set<number>();
  for (const part of s.split(";")) {
    const m = /^([1-7])=(.*)$/.exec(part.trim());
    if (!m) return null;
    const d = Number(m[1]) as Weekday;
    if (seen.has(d)) return null;
    seen.add(d);
    if (!m[2]) continue;
    for (const iv of (m[2] ?? "").split(",")) {
      const [open = "", close = ""] = iv.split("-");
      if (!valid(open) || !valid(close) || open === close) return null;
      week[d].push([open, close]);
    }
    if (week[d].length > 3) return null;
  }
  return week;
}

/** The compact string; "" when no day is open (nothing to show). */
export function formatHours(week: Week): string {
  if (WEEK.every((d) => !week[d].length)) return "";
  return WEEK.map((d) => `${d}=${week[d].map(([o, c]) => `${o}-${c}`).join(",")}`).join(";");
}

export const isValidHours = (s: string) => parseHours(s) !== null;

/**
 * Lines for display, consecutive days with the same hours grouped: "Lun–Vin" + "12:00–23:00",
 * "Dum" + closed. `days` are the short names Monday…Sunday.
 */
export function hoursLines(
  s: string,
  days: readonly string[],
  closed: string,
): { days: string; hours: string }[] {
  const week = parseHours(s);
  if (!week || WEEK.every((d) => !week[d].length)) return [];
  const label = (d: Weekday) => week[d].map(([o, c]) => `${o}–${c}`).join(", ") || closed;
  const lines: { days: string; hours: string }[] = [];
  let start: Weekday = 1;
  for (const d of WEEK) {
    const next = (d + 1) as Weekday;
    if (d === 7 || label(next) !== label(d)) {
      const name = (x: Weekday) => days[x - 1] ?? String(x);
      lines.push({ days: start === d ? name(d) : `${name(start)}–${name(d)}`, hours: label(d) });
      start = next;
    }
  }
  return lines;
}

/** Day of week (1 = Monday) and "HH:MM" at `now` in an IANA time zone. */
export function localWeekTime(now: number, timeZone: string): { day: Weekday; time: string } {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(now));
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  const day = (["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].indexOf(get("weekday")) + 1 || 1) as Weekday;
  return { day, time: `${get("hour")}:${get("minute")}` };
}

/**
 * Whether a schedule is open on `day` at `time`. An interval whose closing time is not after its opening time
 * runs past midnight into the next day. An empty schedule is always open.
 */
export function isOpenAt(s: string, day: Weekday, time: string): boolean {
  const week = parseHours(s);
  if (!week || WEEK.every((d) => !week[d].length)) return true;
  const prev = (day === 1 ? 7 : day - 1) as Weekday;
  for (const [o, c] of week[day]) {
    if (o < c ? time >= o && time < c : time >= o) return true;
  }
  for (const [o, c] of week[prev]) {
    if (!(o < c) && time < c) return true;
  }
  return false;
}
